import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck, DownloadFileFn} from '../../helpers'
import {
  DashboardNotificationClient,
  SearchNotificationClient,
  CompleteNotificationClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

// API Clients
const dashboardNotificationClient = new DashboardNotificationClient(SERVICE_URL)
const searchNotificationClient = new SearchNotificationClient(SERVICE_URL)
const completeNotificationClient = new CompleteNotificationClient(SERVICE_URL)

const initialState = {
  loading: false,
  error: null,
  excel: null,
  complete: false,
  notificationDashboard: null,
  notification: null,
}

// Async Thunks
export const notificationGetDashboard = createAsyncThunk(
  'notification/getDashboard',
  async (_, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await dashboardNotificationClient.get()
      dispatch(setNotificationDashboard(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const notificationGetAll = createAsyncThunk(
  'notification/getAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await searchNotificationClient.search(
        params.content,
        params.notificationTypeId,
        params.isCompleted,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setNotification(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const notificationExcel = createAsyncThunk(
  'notification/excel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await searchNotificationClient.excel(
        params.content,
        params.notificationTypeId,
        params.isCompleted,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const notificationComplete = createAsyncThunk(
  'notification/complete',
  async (id, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await completeNotificationClient.complete(id)
      dispatch(setComplete(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// Slice
const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setNotificationDashboard: (state, action) => {
      state.notificationDashboard = action.payload
      state.loading = false
    },
    setNotification: (state, action) => {
      state.notification = action.payload
      state.loading = false
    },
    setExcel: (state, action) => {
      state.excel = action.payload
      state.loading = false
    },
    setComplete: (state, action) => {
      state.complete = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('notification/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

// Export actions
export const {
  setLoading,
  setError,
  setNotificationDashboard,
  setNotification,
  setExcel,
  setComplete,
  resetState,
} = notificationSlice.actions

// Selectors
export const selectNotificationLoading = (state) => state.notification.loading
export const selectNotificationError = (state) => state.notification.error
export const selectNotificationDashboard = (state) => state.notification.notificationDashboard
export const selectNotificationList = (state) => state.notification.notification
export const selectNotificationExcel = (state) => state.notification.excel
export const selectNotificationComplete = (state) => state.notification.complete

export default notificationSlice.reducer
